// STYLES used to be a static template string. It's now a function of the
// current palette (getStyles(T)) so the handful of color values baked into
// raw CSS text (body background, scrollbar, focus outline) actually change
// when applyTheme() below mutates T — a plain exported string could never
// react to that, since <style>{STYLES}</style> would just keep re-inserting
// the same frozen text every render regardless of what T's properties are
// at the time. Everywhere else in the app, T.xxx is read directly inside
// inline styles at render time, so mutating T's properties in place is
// enough on its own — this function only exists to extend that same trick
// to the small amount of real CSS text in this file.
// No @font-face here. public/index.html links public/fonts/fonts.css,
// which declares the faces once for every screen -- including the
// signed-out ones this function never reaches. Repeating them here would
// re-declare the same 24 faces in a <style> tag on every render.
export const getStyles = (T) => `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body,#root{background:${T.bg};min-height:100vh}
.hf{font-family:'Plus Jakarta Sans',sans-serif;background:${T.bg};min-height:100vh;color:${T.text}}
.mono{font-family:'DM Mono',monospace}
@keyframes fadeDown{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes bidIn{from{opacity:0;transform:translateX(-6px)}to{opacity:1;transform:translateX(0)}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
@keyframes slideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes clydeIn{from{transform:translateX(105%);opacity:0}to{transform:translateX(0);opacity:1}}
@keyframes clydeTyping{0%,80%,100%{transform:scale(0.7);opacity:0.4}40%{transform:scale(1);opacity:1}}
@keyframes clydePulse{0%,100%{box-shadow:0 0 0 0 rgba(74,158,204,0.5)}60%{box-shadow:0 0 0 10px rgba(74,158,204,0)}}
.ci2:focus{outline:none}.ci2::placeholder{color:#4A6080}
.fade-up{animation:fadeUp .38s cubic-bezier(.22,1,.36,1) both}
.fade-dn{animation:fadeDown .32s cubic-bezier(.22,1,.36,1) both}
.bid-in{animation:bidIn .3s cubic-bezier(.22,1,.36,1) both}
.slide-up{animation:slideUp .35s cubic-bezier(.22,1,.36,1) both}
.spin{animation:spin .75s linear infinite}
.blink{animation:pulse 1.8s ease-in-out infinite}
::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:${T.borderMd};border-radius:4px}
input,button,select,textarea{font-family:inherit}
input:focus,select:focus,textarea:focus{outline:2px solid ${T.teal};outline-offset:1px;border-color:transparent!important}
button{cursor:pointer;transition:opacity .15s,box-shadow .15s}button:hover{opacity:.92}button:active{opacity:.8}
.hf-modal-wrap{position:fixed;inset:0;z-index:900;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.45);backdrop-filter:blur(8px);padding:16px}
.hf-modal{background:${T.card};border-radius:20px;box-shadow:0 4px 12px rgba(0,0,0,.08);overflow:hidden;display:flex;flex-direction:column;width:min(620px,100%);max-height:92vh}
.hf-modal-sm{background:${T.card};border-radius:20px;box-shadow:0 4px 12px rgba(0,0,0,.08);overflow:hidden;width:min(500px,100%)}
.hf-bid-modal{width:min(620px,96vw);background:${T.card};border-radius:20px;box-shadow:0 24px 60px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column}
.hf-scanner-modal{width:min(500px,100%);background:${T.card};border-radius:20px;box-shadow:0 4px 12px rgba(0,0,0,.08);overflow:hidden;display:flex;flex-direction:column;max-height:92vh}
.hf-maint-label{display:inline}
.hf-task-check{width:22px;height:22px;border-radius:50%;flex-shrink:0;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}

/* ===== Portfolio header (HomeFleetApp) ===== */
.hf-portfolio-line{display:grid;grid-template-columns:repeat(5,1fr);gap:18px 16px}
.hf-portfolio-tabs{display:flex;gap:8px;margin-top:18px;padding-top:16px;border-top:1px solid ${T.border}}
.hf-ptab{flex:1;background:${T.slateL};border:1.5px solid ${T.border};border-radius:10px;padding:10px 14px;font-size:13px;font-weight:700;color:${T.textMd};text-align:left;display:flex;flex-direction:column;gap:2px}
.hf-ptab.active{background:${T.tealL};border-color:${T.tealBorderStrong};color:${T.teal}}
.hf-ptab:hover{border-color:${T.borderMd}}
.hf-pbreakdown{margin-top:12px;background:${T.slateL};border:1px solid ${T.border};border-radius:12px;padding:14px 16px}
.hf-pbreakdown-row{display:flex;justify-content:space-between;align-items:center;padding:7px 0;font-size:13px}
.hf-pbreakdown-row:not(:last-child){border-bottom:1px dashed ${T.border}}

/* ===== Vehicle card header ===== */
.hf-vcard{background:${T.card};border-radius:16px;border:1.5px solid ${T.border};box-shadow:${T.shadow};overflow:hidden;transition:box-shadow .2s}
.hf-vcard:hover{box-shadow:${T.shadowMd}}
.hf-vcard-id{display:flex;align-items:center;gap:12px;padding:14px 20px 10px}
.hf-vphoto{width:46px;height:46px;border-radius:12px;background:linear-gradient(135deg,${T.slateL},${T.border});display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}
.hf-vsub{display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:12px;color:${T.textMd}}
.hf-vsub .dot{color:${T.textSm}}
.hf-icon-btn{width:30px;height:30px;border-radius:8px;background:${T.slateL};border:1.5px solid ${T.border};color:${T.textMd};display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0}
.hf-icon-btn:hover{background:${T.tealL};color:${T.teal};border-color:${T.tealBorder}}
.hf-icon-btn.danger:hover{background:${T.redL};color:${T.red};border-color:${T.redBorderStrong}}
.hf-alert-badge{position:relative;display:inline-flex;align-items:center;font-size:11px;font-weight:700;border-radius:6px;padding:3px 9px;white-space:nowrap;cursor:default}
.hf-alert-badge[data-tip]:hover::after{
  content:attr(data-tip);position:absolute;bottom:calc(100% + 7px);left:0;background:${T.slate};color:#fff;
  font-size:11px;font-weight:600;padding:6px 10px;border-radius:7px;white-space:nowrap;box-shadow:${T.shadowMd};z-index:20;
  animation:fadeUp .15s ease-out both;
}
.hf-alert-badge[data-tip]:hover::before{
  content:'';position:absolute;bottom:calc(100% + 3px);left:12px;border:5px solid transparent;border-top-color:${T.slate};z-index:20;
}

/* ===== Finance strip (top of the Financial tab) ===== */
.hf-fin-strip{display:grid;grid-template-columns:1fr 1fr 1fr;gap:7px}
.hf-fin-cell{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;background:${T.slateL};border:1.5px solid ${T.border};border-radius:9px;padding:7px 8px;text-align:center;line-height:1.2;transition:all .15s}
.hf-fin-cell.has-save{background:${T.tealSoftBg};border-color:${T.tealBorder}}
.hf-fin-cell:hover{border-color:${T.borderMd};transform:translateY(-1px)}
.hf-fin-cell.has-save:hover{border-color:${T.tealBorderStrong}}
.hf-fin-detail{padding:12px 14px;background:${T.card};border:1px dashed ${T.border};border-radius:10px;font-size:12.5px;color:${T.textMd};line-height:1.6}

/* ===== Section tabs — individually elevated pill buttons, not a flat
   segmented strip — each one needs to read as its own clickable button
   rather than blend into the card underneath it ===== */
.hf-sec-tabs{display:flex;gap:8px;margin:0 20px 14px}
.hf-sec-tab{flex:1;background:${T.slateL};border:1.5px solid ${T.border};border-radius:10px;box-shadow:${T.shadow};padding:9px 4px;font-size:11px;font-weight:700;color:${T.textMd};text-align:center;line-height:1.25;white-space:normal;transition:box-shadow .15s,transform .15s,background .15s}
.hf-sec-tab:hover{background:${T.card};color:${T.text};box-shadow:${T.shadowMd};transform:translateY(-1px)}
.hf-sec-tab.active{background:${T.slate};border-color:${T.slate};color:#fff;box-shadow:${T.shadowMd}}
.hf-sec-tab.hint{box-shadow:inset 0 0 0 2px var(--hf-hint-color,${T.teal}),${T.shadow};position:relative;z-index:1;color:${T.text}}
.hf-sec-panel{border-top:1.5px solid ${T.border};padding:16px 20px;background:${T.cardAlt}}
.hf-grid3{display:grid;grid-template-columns:1fr 1fr 1fr}

@media(max-width:640px){
.hf-maint-label{display:none}
.hf-task-check{width:32px;height:32px}
.hf-header-email{display:none}

.hf-portfolio-line{grid-template-columns:1fr 1fr;gap:14px 12px}
.hf-portfolio-tabs{gap:6px}
.hf-ptab{padding:9px 10px;font-size:12px}

.hf-vcard-id{padding:12px 14px 8px;flex-wrap:wrap}
.hf-vid-main{flex-basis:100%;order:3;margin-top:8px}
.hf-sec-tabs{margin:0 14px 14px;overflow-x:auto;-webkit-overflow-scrolling:touch}
.hf-sec-tab{flex:0 0 auto;white-space:nowrap;padding:9px 16px}
.hf-sec-panel{padding:14px}
.hf-grid3{grid-template-columns:1fr 1fr}
}
`;

// Light palette — unchanged values, just moved into a named LIGHT const so
// applyTheme() below has something to restore. T itself stays the single
// export every component already imports; toggling theme mutates T's own
// properties in place (Object.assign) rather than swapping the exported
// binding, so it doesn't matter whether a file did `import {T}` and reads
// T.xxx at render time (every existing usage) — the very next render sees
// the new values with no per-file changes required.
const LIGHT = {
  bg:"#f0f4f8", card:"#ffffff", cardAlt:"#f8fafc", border:"#e2e8f0", borderMd:"#cbd5e1",
  text:"#1e293b", textMd:"#475569", textSm:"#94a3b8",
  // A third, in-between muted tier — GateScreen.jsx/ResetPasswordScreen.jsx
  // hardcoded #64748b for secondary copy (e.g. "Or email ... directly"),
  // which sits between textMd and textSm and doesn't exactly match either,
  // so it gets its own token rather than being silently rounded to one.
  textMuted:"#64748b",
  teal:"#0d9488", tealL:"#ccfbf1", tealMd:"#14b8a6",
  slate:"#334155", slateL:"#f1f5f9",
  amber:"#b45309", amberL:"#fef3c7", amberMd:"#f59e0b",
  red:"#dc2626", redL:"#fee2e2",
  green:"#16a34a", greenL:"#dcfce7",
  blue:"#1d6fa8", blueL:"#dbeafe", blueMd:"#3b82f6",
  purple:"#7c3aed", purpleL:"#ede9fe",
  shadow:"0 1px 3px rgba(0,0,0,.07),0 1px 2px rgba(0,0,0,.05)",
  shadowMd:"0 4px 12px rgba(0,0,0,.08),0 1px 3px rgba(0,0,0,.05)",
  // Accent border/soft-bg tokens the redesign needs that the old inline
  // styles used to hardcode as one-off hex literals (#99f6e4, #5eead4,
  // #fca5a5, #fcd34d, #f0fdfa) scattered through VehicleRow.jsx — named
  // here so both palettes carry them and dark mode isn't just the six
  // original hues with everything else silently staying light-colored.
  tealBorder:"#99f6e4", tealBorderStrong:"#5eead4", tealSoftBg:"#f0fdfa",
  redBorderStrong:"#fca5a5", amberBorderStrong:"#fcd34d", greenBorder:"#86efac",
  btnDisabledBg:"#e2e8f0",
};

const DARK = {
  bg:"#0a0f1c", card:"#151d30", cardAlt:"#10172a", border:"#262f47", borderMd:"#39445e",
  text:"#e8edf5", textMd:"#a9b4c9",
  // Was #64708a — measured contrast against --card (#151d30) came out to
  // ~3.4:1, below WCAG AA's 4.5:1 floor for normal text, and the likely
  // real cause of onboarding/login text reading as "too light" in dark
  // mode (T.text and T.textMd both measure well above AA — 14.3:1 and
  // 8.05:1 respectively — so they weren't the actual problem). #94a3b8
  // (reused from LIGHT.textSm, a color already proven readable in this
  // system) measures ~6.55:1 against --card — comfortably past AA.
  textSm:"#94a3b8",
  textMuted:"#9aa7bd", // ~6.9:1 vs --card — sits between textMd (8.05:1) and textSm (6.55:1), same ordering as LIGHT's three tiers
  teal:"#2dd4bf", tealL:"rgba(45,212,191,.16)", tealMd:"#2dd4bf",
  slate:"#334155", slateL:"#1a2338",
  amber:"#fbbf24", amberL:"rgba(251,191,36,.16)", amberMd:"#f59e0b",
  red:"#f87171", redL:"rgba(248,113,113,.16)",
  green:"#4ade80", greenL:"rgba(74,222,128,.16)",
  blue:"#5b9bd5", blueL:"rgba(91,155,213,.16)", blueMd:"#5b9bd5",
  purple:"#a78bfa", purpleL:"rgba(167,139,250,.16)",
  shadow:"0 1px 3px rgba(0,0,0,.4),0 1px 2px rgba(0,0,0,.3)",
  shadowMd:"0 4px 14px rgba(0,0,0,.5),0 1px 4px rgba(0,0,0,.35)",
  tealBorder:"rgba(45,212,191,.35)", tealBorderStrong:"rgba(45,212,191,.55)", tealSoftBg:"rgba(45,212,191,.08)",
  redBorderStrong:"rgba(248,113,113,.45)", amberBorderStrong:"rgba(251,191,36,.45)", greenBorder:"rgba(74,222,128,.45)",
  btnDisabledBg:"#232c44",
};

// Dark is the default for every account until they explicitly choose
// otherwise (and that choice then persists via display_prefs) — so T
// itself starts as a DARK clone, not LIGHT, meaning even the very first
// paint before any theme logic runs (the login/reset screens, the
// loading spinner before /api/me resolves) renders dark by default too,
// with no flash of a light screen first.
export const T = { ...DARK };

// Mutates T's own properties in place (not a reassignment of the exported
// binding) so every file's existing `import {T} from "../lib/theme"` keeps
// working unchanged — same object reference throughout the app's lifetime,
// just different values on it. The caller (HomeFleetApp) is expected to
// force a re-render right after calling this (e.g. its own useState toggle)
// so every component's next render picks up the new values; mutating T
// alone doesn't itself trigger React to re-render anything.
export function applyTheme(mode) {
  Object.assign(T, mode === "dark" ? DARK : LIGHT);
}
